/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import * as React from "react";
import FreeEnhanceSpeechContainer from "components/EnhancedSpeech/FreeTierEnhanceSpeech/FreeEnhanceSpeechContainer";
import { HomeColumn } from "components/shared/Splash";
import {
  QuickToolType,
  QuickToolsSize,
  BetaBadge,
} from "components/shared/QuickTools";
import { AppFooter } from "components/shared/AppFooter";
import {
  CallToAction,
  PlanTitleContainer,
  PodcastLogo,
  PremiumLogo,
} from "components/Plans/Plan";
import { SplashCopy } from "components/Plans/Plans";
import { PurchaseExpressButton } from "components/PurchaseExpress";
import { ExpressCheckoutButtonPosition } from "constants/Analytics";
import { PremiumIcon } from "components/shared/PremiumIcon";
import iconCheck from "images/check-light.svg";
import { EnhancementObject, useEnhanceSpeech } from "../useEnhanceSpeech";
import { useCurrentUser } from "actions/CurrentUserActions";
import { UpgradeFeatures } from "components/UpgradeFeatures";
import { ProductFeature } from "components/Plans/ProductFeature";
import {
  EnhancePremiumFeatureList,
  ExpressPremiumFeatureList,
  ProductList,
  StudioFeatureList,
} from "components/Plans/ProductList";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { useHistory } from "react-router-dom";
import { useNavigateToStudio } from "utilities/AuthenticatedNavigation";
import { useMemo } from "react";
import { isMobileSize } from "components/shared/Styles";

import enhanceThumbnailBig1x from "images/home/landing/toolsthumbnails/enhance-thumbnail-big@1x.jpg";
import enhanceThumbnailBig2x from "images/home/landing/toolsthumbnails/enhance-thumbnail-big@2x.jpg";
import micCheckThumbnailBig1x from "images/home/landing/toolsthumbnails/mic-check-thumbnail-big@1x.jpg";
import micCheckThumbnailBig2x from "images/home/landing/toolsthumbnails/mic-check-thumbnail-big@2x.jpg";
import studioThumbnailBig1x from "images/home/landing/toolsthumbnails/studio-thumbnail-big@1x.jpg";
import studioThumbnailBig2x from "images/home/landing/toolsthumbnails/studio-thumbnail-big@2x.jpg";

type ButtonSize = "s" | "m" | "l" | "xl";

const ContentRow = styled(HomeColumn)`
  gap: 16px;
  display: flex;
  position: relative;
  z-index: 1;
  justify-content: space-between;
  width: calc(100% - 40px);

  ${devices.medium} {
    margin: 0px 20px;
  }

  ${devices.small} {
    flex-direction: column;
    text-align: center;
    align-items: center;
    box-sizing: border-box;
  }
`;

const EnhanceRow = styled(ContentRow)`
  ${devices.small} {
    margin-top: -80px;
  }
`;

const ToolsRow = styled.div`
  margin: 0px auto 108px;
  max-width: 1156px;
  width: calc(100% - 40px);
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 48px;
  ${devices.small} {
    width: calc(100% - 48px);
    row-gap: 32px;
    grid-template-columns: 1fr;
  }
`;

const ToolsHeader = styled.h1`
  grid-column: span 2;
  text-align: center;
  height: 45px;
  padding-bottom: 42px;
  font: normal normal 900 36px/45px ${fontFamilies};
  letter-spacing: 0px;
  color: #222222;
  opacity: 1;
  ${devices.small} {
    padding-bottom: 16px;
    text-align: left;
    grid-column: span 1;
    font: normal normal 900 28px/32px ${fontFamilies};
  }
`;

const Tools: React.FC<{
  toolsToShow: QuickToolType[];
  imageAspect?: number;
}> = ({ toolsToShow, imageAspect = 1.25 }) => {
  const history = useHistory();
  const { navigateToStudio } = useNavigateToStudio();
  const badgeSize = useMemo((): ButtonSize => {
    if (isMobileSize()) {
      return "s";
    }
    return "l";
  }, []);

  const availableTools = {
    [QuickToolType.EnhanceSpeech]: (
      <ToolSectionContainer
        key={QuickToolType.EnhanceSpeech}
        onClick={(): void => history.push("/enhance")}
      >
        <TextContainer>
          <ImageContainer>
            <ToolImage
              imageAspect={imageAspect}
              srcSet={`${enhanceThumbnailBig1x} 1x, ${enhanceThumbnailBig2x} 2x`}
              alt=""
            />
          </ImageContainer>
          <ToolTitle>{I18n.t("landing.tools_lineup.enhance.title")}</ToolTitle>
          <ToolSubtitle>
            {I18n.t("landing.tools_lineup.enhance.subtitle")}
          </ToolSubtitle>
        </TextContainer>
        <sp-button href="#" treatment="fill" variant="primary" size={badgeSize}>
          <ButtonLabelText>
            {I18n.t("landing.tools_lineup.enhance.action")}
          </ButtonLabelText>
        </sp-button>
      </ToolSectionContainer>
    ),
    [QuickToolType.MicCheck]: (
      <ToolSectionContainer
        key={QuickToolType.MicCheck}
        onClick={(): void => history.push("/miccheck")}
      >
        <TextContainer>
          <ImageContainer>
            <ToolImage
              imageAspect={imageAspect}
              srcSet={`${micCheckThumbnailBig1x} 1x, ${micCheckThumbnailBig2x} 2x`}
              alt=""
            />
          </ImageContainer>
          <ToolTitle>
            {I18n.t("landing.tools_lineup.mic_check.title")}
          </ToolTitle>
          <ToolSubtitle>
            {I18n.t("landing.tools_lineup.mic_check.subtitle")}
          </ToolSubtitle>
        </TextContainer>
        <sp-button href="#" treatment="fill" variant="primary" size={badgeSize}>
          <ButtonLabelText>
            {I18n.t("landing.tools_lineup.mic_check.action")}
          </ButtonLabelText>
        </sp-button>
      </ToolSectionContainer>
    ),
    [QuickToolType.Studio]: (
      <ToolSectionContainer
        key={QuickToolType.Studio}
        onClick={navigateToStudio}
      >
        <TextContainer>
          <ImageContainer>
            <ToolImage
              imageAspect={imageAspect}
              srcSet={`${studioThumbnailBig1x} 1x, ${studioThumbnailBig2x} 2x`}
              alt=""
            />
          </ImageContainer>
          <ToolTitle>
            {I18n.t("landing.tools_lineup.studio.title")}
            <BetaBadge />
          </ToolTitle>
          <ToolSubtitle>
            {I18n.t("landing.tools_lineup.studio.subtitle")}
          </ToolSubtitle>
        </TextContainer>
        <sp-button href="#" treatment="fill" variant="primary" size={badgeSize}>
          <ButtonLabelText>
            {I18n.t("landing.tools_lineup.studio.action.go_to_studio")}
          </ButtonLabelText>
        </sp-button>
      </ToolSectionContainer>
    ),
  };
  return <>{toolsToShow.map((tool) => availableTools[tool])}</>;
};

const ButtonLabelText = styled.div`
  white-space: normal;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ToolImage = styled.img<{ imageAspect: number }>`
  border-radius: 32px;
  width: 100%;
  margin-bottom: 16px;
  object-fit: cover;
  aspect-ratio: ${({ imageAspect }): number => imageAspect};

  ${devices.small} {
    max-height: 216px;
    border-radius: 24px;
  }
`;

const ToolTitle = styled.span`
  display: flex;
  flex-direction: row;
  font: normal normal 800 22px/27px ${fontFamilies};
  color: var(--spectrum-global-color-gray-900);
  gap: 8px;
  margin-bottom: 8px;
`;

const ToolSubtitle = styled.span`
  font: normal normal normal 18px/22px ${fontFamilies};
  color: var(--spectrum-global-color-gray-900);
  margin-bottom: 16px;
`;

const ToolSectionContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  cursor: pointer;
`;

const Enhance: React.FC<{
  enhancement: EnhancementObject | undefined;
  processFile: (droppedFile: File, modelVersion: ModelVersion) => void;
}> = ({ enhancement, processFile }) => {
  return (
    <EnhanceRow data-testid="enhance-content-row">
      <FreeEnhanceSpeechContainer
        enhancement={enhancement}
        processFile={processFile}
      />
    </EnhanceRow>
  );
};

const UpgradeRow = styled(ContentRow)`
  background: var(--spectrum-global-color-static-indigo-600);
  color: white;
  border-radius: 32px;
  height: 100%;
  align-items: center;

  ${devices.small} {
    border-radius: 24px;
    text-align: initial;
  }
`;

const PremiumFeaturesPanel = styled.div`
  border-radius: 24px;
  background-color: var(--spectrum-global-color-static-indigo-700);
  padding: 20px 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 8px;
  flex: 1;
  width: 426px; // 538 - 2 * 56

  ${devices.medium} {
    padding: 22px;
  }

  ${devices.small} {
    border-radius: 16px;
    padding: 18px;
    width: calc(100% - 68px);
    height: unset;
    margin: 16px;
  }
`;
const PremiumFeaturesContainer = styled.div`
  align-self: center;
  margin: 36px 0;

  ${devices.medium} {
    margin: 0px;
  }

  ${devices.small} {
    margin: 0px;
  }
`;

const PremiumFeaturesPanelTitle = styled(PlanTitleContainer)`
  margin-bottom: 16px;

  ${devices.small} {
    display: none;
  }
`;

const UpgradeCallout = styled(SplashCopy)`
  margin: 24px 72px;
  font-weight: 300;
  flex: 1;
  ${devices.medium} {
    margin: 24px 36px;
  }
  ${devices.small} {
    margin-top: 24px;
    margin-bottom: 14px;
    width: calc(100% - 48px);
  }
`;

const UpgradeTitle = styled.h1`
  font: normal normal 900 45px/48px ${fontFamilies};
  color: var(--spectrum-global-color-static-white);

  ${devices.small} {
    font-size: 34px;
    line-height: 38px;
  }
`;

const UpgradeSubtitle = styled.div`
  margin-top: 20px;
  margin-bottom: 24px;
  font: normal normal normal 18px/22px ${fontFamilies};
  color: var(--spectrum-global-color-static-white);

  ${devices.small} {
    margin-top: 6px;
    margin-bottom: 34px;
  }
`;

const MobilePremiumLogo = styled(PremiumLogo)`
  vertical-align: text-top;
  display: none;

  ${devices.small} {
    display: unset;
  }
`;

const SubscriptionReminderCallout = styled.div`
  margin-top: 4px;
  margin-left: -4px;
`;

const Upgrade: React.FC = () => {
  const products = [
    EnhancePremiumFeatureList,
    StudioFeatureList,
    ExpressPremiumFeatureList,
  ];

  return (
    <UpgradeRow>
      <UpgradeCallout data-testid="plans-splash-copy">
        <UpgradeTitle>
          {I18n.t("plans.title")}
          <MobilePremiumLogo />
        </UpgradeTitle>
        <UpgradeSubtitle>{I18n.t("plans.imperative_subtitle")}</UpgradeSubtitle>
        <CallToAction data-testid="call-to-action">
          <PurchaseExpressButton
            color={"white"}
            position={ExpressCheckoutButtonPosition.FREE_ENHANCE_UPGRADE_ROW}
          >
            <PremiumIcon slot="icon" size={20} />
            {I18n.t("plans.premium.button")}
          </PurchaseExpressButton>
          <SubscriptionReminderCallout>
            <ProductFeature image={iconCheck}>
              {I18n.t("plans.premium.reminder")}
            </ProductFeature>
          </SubscriptionReminderCallout>
        </CallToAction>
      </UpgradeCallout>
      <PremiumFeaturesPanel data-testid="premium-features-panel">
        <PremiumFeaturesContainer>
          <PremiumFeaturesPanelTitle data-testid="premium-features-panel-title">
            <PodcastLogo />
            <span>{I18n.t("plans.premium.title")}</span>
            <PremiumLogo />
          </PremiumFeaturesPanelTitle>
          <ProductList products={products} />
        </PremiumFeaturesContainer>
      </PremiumFeaturesPanel>
    </UpgradeRow>
  );
};

export const FreeTierEnhanceSpeech: React.FC = () => {
  const { currentUser } = useCurrentUser();
  const { processFile, enhancements } = useEnhanceSpeech();
  const enhancement: EnhancementObject | undefined = enhancements[0];
  const isMobile = useIsMobile();

  const hideUpgrade = currentUser && enhancement && isMobile;
  return (
    <>
      <Enhance enhancement={enhancement} processFile={processFile} />
      {!hideUpgrade && <Upgrade />}
      <UpgradeFeatures
        position={ExpressCheckoutButtonPosition.FREE_ENHANCED_SPEECH_BENTO_BOX}
      />
      <ToolsRow>
        <ToolsHeader>{I18n.t("landing.tools_lineup.heading")}</ToolsHeader>
        <Tools
          toolsToShow={[QuickToolType.MicCheck, QuickToolType.Studio]}
          imageAspect={2}
        />
      </ToolsRow>
      <AppFooter />
    </>
  );
};
